import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  BellIcon, 
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

const NotificationDropdown = ({ 
  notifications = [], 
  unreadNotifications = 0,
  showNotifications, 
  setShowNotifications,
  onMarkNotificationRead,
  onMarkAllNotificationsRead
}) => {
  return (
    <div className="relative">
      <motion.button 
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowNotifications(!showNotifications)}
        className={`relative p-2.5 rounded-full transition-all duration-300 ${
          showNotifications 
            ? 'bg-orange-50 text-orange-600 ring-2 ring-orange-100' 
            : 'bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-900 border border-transparent'
        }`}
      >
        <BellIcon className="h-5 w-5" />
        {unreadNotifications > 0 && (
          <span className="absolute top-0 right-0 h-4 w-4 bg-orange-500 text-[10px] font-black text-white rounded-full flex items-center justify-center ring-2 ring-white">
            {unreadNotifications > 9 ? '9+' : unreadNotifications}
          </span>
        )}
      </motion.button>
      
      <AnimatePresence>
        {showNotifications && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/5 z-40" 
              onClick={() => setShowNotifications(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, y: 15, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
              className="absolute right-0 top-full mt-4 w-80 sm:w-[420px] bg-white rounded-[32px] shadow-2xl shadow-gray-200 border border-gray-100 z-50 overflow-hidden"
            >
              {/* Header */}
              <div className="px-8 py-6 border-b border-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-black text-gray-900 tracking-tight">Activity</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Your latest updates</p>
                  </div>
                  {unreadNotifications > 0 && (
                    <span className="px-3 py-1 bg-orange-50 text-orange-600 text-[10px] font-black rounded-full uppercase tracking-widest">
                      {unreadNotifications} NEW
                    </span>
                  )}
                </div>
              </div>
              
              {/* List */}
              <div className="max-h-[480px] overflow-y-auto scrollbar-hide">
                {notifications.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {notifications.map((notification, index) => {
                      const getNotificationIcon = (type) => {
                        switch (type) {
                          case 'new_order':
                            return <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center"><InformationCircleIcon className="h-5 w-5 text-blue-500" /></div>
                          case 'order_assigned':
                            return <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center"><CheckCircleIcon className="h-5 w-5 text-emerald-500" /></div>
                          case 'order_accepted':
                            return <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center"><CheckCircleIcon className="h-5 w-5 text-emerald-500" /></div>
                          case 'order_rejected':
                            return <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center"><XCircleIcon className="h-5 w-5 text-rose-500" /></div>
                          case 'no_staff_available':
                            return <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center"><ExclamationTriangleIcon className="h-5 w-5 text-amber-500" /></div>
                          default:
                            return <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center"><BellIcon className="h-5 w-5 text-gray-500" /></div>
                        }
                      }

                      return (
                        <motion.div 
                          key={notification.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`px-8 py-5 transition-all duration-300 cursor-pointer group hover:bg-gray-50/50 ${!notification.is_read && 'bg-orange-50/20'}`}
                          onClick={() => onMarkNotificationRead?.(notification.id)}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1 transition-transform group-hover:scale-110 duration-500">
                              {getNotificationIcon(notification.notification_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm leading-snug tracking-tight ${notification.is_read ? 'text-gray-500' : 'text-gray-900 font-bold'}`}>
                                {notification.message}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                                   {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {new Date(notification.created_at).toLocaleDateString()}
                                </p>
                                {!notification.is_read && (
                                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full ring-4 ring-orange-100"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="px-8 py-20 text-center">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <BellIcon className="w-10 h-10 text-gray-200" />
                    </div>
                    <p className="text-gray-900 font-black tracking-tight uppercase tracking-widest text-xs">Peace & Quiet</p>
                    <p className="text-gray-400 text-[10px] font-medium mt-1">No new notifications at the moment</p>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              {notifications.length > 0 && (
                <div className="px-8 py-4 bg-gray-50/50 flex justify-between items-center">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {notifications.length} Total
                  </span>
                  {unreadNotifications > 0 && (
                    <button 
                      onClick={() => onMarkAllNotificationsRead?.()}
                      className="text-[10px] font-black text-orange-600 hover:text-orange-700 uppercase tracking-widest px-3 py-1 rounded-lg hover:bg-orange-50 transition-colors"
                    >
                      CLEAR ALL
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationDropdown
